import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import axios from '../utils/axiosConfig';
import {errorHandler} from '../utils/errorHandler';
import {setUserPreferences, setUserPreferencesLoading} from '../redux/slices/organization';

export interface UserPreferenceItem {
  id: number;
  category: string;
  key: string;
  value?: any;
  text?: string;
  updated_at?: string;
  confidence_score?: number;
  created_at?: string;
  last_accessed_at?: string;
}

export interface UserPreferencesResponse {
  preferences: UserPreferenceItem[];
}

const useUserPreferencesService = () => {
  const dispatch = useDispatch();

  const { userPreferences, userPreferencesLoading } = useSelector(
    (state: any) => state.organization
  );

  const showError = (error: any) => {
    errorHandler.showError(error);
  };

  const fetchUserPreferences = useCallback(async (org_id: string, options?: { category?: string | null; include_summary?: boolean; limit?: number; }) => {
    if (!org_id) return [] as UserPreferenceItem[];
    dispatch(setUserPreferencesLoading(true));
    try {
      const params: any = {};
      if (options?.category) params.category = options.category;
      if (typeof options?.include_summary === 'boolean') params.include_summary = options.include_summary;
      if (typeof options?.limit === 'number') params.limit = options.limit;

      const response = await axios.get<UserPreferencesResponse>(`/organization/${org_id}/user-preferences`, { params });
      const list = response?.data?.preferences || [];
      dispatch(setUserPreferences(list));
      return list;
    } catch (error) {
      showError(error);
      return [] as UserPreferenceItem[];
    } finally {
      dispatch(setUserPreferencesLoading(false));
    }
  }, [dispatch]);

  const deleteUserPreferences = useCallback(async (org_id: string, factIds: string[]): Promise<{ success: boolean; message: string }> => {
    if (!org_id || !factIds || factIds.length === 0) return { success: false, message: 'Invalid parameters' };

    try {
      const response = await axios.delete<{ message?: string }>(`/organization/${org_id}/user-preferences`, {
        data: { fact_ids: factIds }
      });

      return { success: true, message: response?.data?.message || 'Preferences deleted' };
    } catch (error) {
      showError(error);
      return { success: false, message: 'Failed to delete preferences' };
    }
  }, [dispatch]);

  return {
    loading: userPreferencesLoading,
    preferences: userPreferences,
    fetchUserPreferences,
    deleteUserPreferences,
  };
};

export default useUserPreferencesService;


